import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

import { adminService } from '@/api/services/admin.service';

import { queryKeys } from '../query-keys';

import { useToast } from '@/hooks/use-toast';

import { useLanguage } from '@/contexts/LanguageContext';

import { getErrorMessage, getAllValidationErrors, getFormattedErrors } from '@/api/errors/error-handler';

import type { TenderFilters, OfferFilters, ClientFilters, UpdateClientStatusRequest, CreateTenderRequest, UpdateTenderRequest, CreateClientRequest, UpdateClientRequest } from '@/api/types';



export const useAdminDashboard = (options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: queryKeys.dashboard.admin(),

    queryFn: () => adminService.getDashboard(),

    staleTime: 1 * 60 * 1000, // 1 minute

    retry: 1,

    retryDelay: 1000,

    enabled: options?.enabled !== false,

  });

};



export const useAdminTenders = (filters?: TenderFilters, options?: { enabled?: boolean }) => {

  return useInfiniteQuery({

    queryKey: ['admin', 'tenders', filters],

    queryFn: ({ pageParam = 1 }) => adminService.getTenders({ ...filters, page: pageParam } as any),

    getNextPageParam: (lastPage) => {

      const pagination = lastPage?.data?.pagination;

      if (!pagination) return undefined;

      const currentPage = pagination.page || 1;

      const totalPages = pagination.totalPages || 0;

      return currentPage < totalPages ? currentPage + 1 : undefined;

    },

    initialPageParam: 1,

    staleTime: 2 * 60 * 1000, // 2 minutes

    enabled: options?.enabled !== false,

    retry: 1,

    retryDelay: 1000,

  });

};



export const useAdminTender = (id: number, options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: ['admin', 'tenders', 'detail', id],

    queryFn: () => adminService.getTenderDetails(id),

    enabled: options?.enabled !== false && !!id,

  });

};



export const useCreateTender = (onSuccess?: () => void) => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (data: CreateTenderRequest) => adminService.createTender(data),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders'] });

      toast({

        title: t('tenders.created') || 'تم إنشاء المناقصة',

        description: t('tenders.createdDesc') || 'تم إنشاء المناقصة بنجاح',

      });

      onSuccess?.();

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      const allErrors = getAllValidationErrors(error);

      const formattedErrors = getFormattedErrors(error);

      // Log all validation errors to console for debugging

      if (allErrors) {

        console.error('Validation errors:', allErrors);

        console.error('Formatted errors:', formattedErrors);

      }

      console.error('Create tender error:', error);

      toast({

        title: t('tenders.createFailed') || 'فشل إنشاء المناقصة',

        description: formattedErrors || errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useUpdateTender = (onSuccess?: () => void) => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: ({ id, data }: { id: number; data: UpdateTenderRequest }) =>

      adminService.updateTender(id, data),

    onSuccess: (_response, variables) => {

      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders'] });

      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders', 'detail', variables.id] });

      toast({

        title: t('tenders.updated') || 'تم تحديث المناقصة',

        description: t('tenders.updatedDesc') || 'تم تحديث المناقصة بنجاح',

      });

      onSuccess?.();

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('tenders.updateFailed') || 'فشل تحديث المناقصة',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useDeleteTender = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (id: number) => adminService.deleteTender(id),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders'] });

      toast({

        title: t('tenders.deleted') || 'تم حذف المناقصة',

        description: t('tenders.deletedDesc') || 'تم حذف المناقصة بنجاح',

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('tenders.deleteFailed') || 'فشل حذف المناقصة',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useAdminOffers = (filters?: OfferFilters, options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: queryKeys.adminOffers.list(filters),

    queryFn: () => adminService.getOffers(filters),

    staleTime: 2 * 60 * 1000, // 2 minutes

    enabled: options?.enabled !== false,

    retry: 1,

    retryDelay: 1000,

  });

};



export const useAdminOffer = (id: number, options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: queryKeys.adminOffers.detail(id),

    queryFn: () => adminService.getOfferDetails(id),

    enabled: options?.enabled !== false && !!id,

  });

};



export const useAcceptOffer = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (id: number) => adminService.acceptOffer(id),

    onSuccess: (_response, variables) => {

      queryClient.invalidateQueries({ queryKey: queryKeys.adminOffers.all });

      queryClient.invalidateQueries({ queryKey: queryKeys.adminOffers.detail(variables) });

      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.admin() });

      toast({

        title: t('offers.acceptedTitle') || 'تم قبول العرض',

        description: t('offers.acceptedDesc') || 'تم قبول العرض بنجاح',

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('offers.acceptFailed') || 'فشل قبول العرض',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useRejectOffer = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (id: number) => adminService.rejectOffer(id),

    onSuccess: (_response, variables) => {

      queryClient.invalidateQueries({ queryKey: queryKeys.adminOffers.all });

      queryClient.invalidateQueries({ queryKey: queryKeys.adminOffers.detail(variables) });

      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.admin() });

      toast({

        title: t('offers.rejectedTitle') || 'تم رفض العرض',

        description: t('offers.rejectedDesc') || 'تم رفض العرض بنجاح',

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('offers.rejectFailed') || 'فشل رفض العرض',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useDeleteAdminOffer = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (id: number) => adminService.deleteOffer(id),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: queryKeys.adminOffers.all });

      queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.admin() });

      toast({

        title: t('offers.deleted') || 'تم حذف العرض',

        description: t('offers.deletedDesc') || 'تم حذف العرض بنجاح',

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('offers.deleteFailed') || 'فشل حذف العرض',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



// Admin Clients

export const useAdminClients = (filters?: ClientFilters, options?: { enabled?: boolean }) => {

  return useInfiniteQuery({

    queryKey: queryKeys.adminClients.list(filters),

    queryFn: ({ pageParam = 1 }) => adminService.getClients({ ...filters, page: pageParam } as any),

    getNextPageParam: (lastPage) => {

      const pagination = lastPage?.data?.pagination;

      if (!pagination) return undefined;

      const currentPage = pagination.page || 1;

      const totalPages = pagination.totalPages || 0;

      return currentPage < totalPages ? currentPage + 1 : undefined;

    },

    initialPageParam: 1,

    staleTime: 2 * 60 * 1000, // 2 minutes

    enabled: options?.enabled !== false,

    retry: 1,

    retryDelay: 1000,

  });

};



export const useAdminClient = (id: number, options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: queryKeys.adminClients.detail(id),

    queryFn: () => adminService.getClientDetails(id),

    enabled: options?.enabled !== false && !!id,

  });

};



export const useUpdateClientStatus = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: ({ id, data }: { id: number; data: UpdateClientStatusRequest }) =>

      adminService.updateClientStatus(id, data),

    onSuccess: (_response, variables) => {

      // Invalidate all client queries to refresh the list

      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.all });

      queryClient.invalidateQueries({

        queryKey: queryKeys.adminClients.detail(variables.id),

      });

      // Also invalidate lists to ensure status updates

      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.lists() });

      const statusLabel = variables.data.status === 'approved' 

        ? t('admin.clients.approved') || 'تم الاعتماد'

        : variables.data.status === 'rejected'

        ? t('admin.clients.rejected') || 'تم الرفض'

        : t('admin.clients.suspended') || 'تم التعليق';

      toast({

        title: t('admin.clients.statusUpdated') || 'تم تحديث الحالة',

        description: statusLabel,

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('admin.clients.updateFailed') || 'فشل التحديث',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useDeleteClient = () => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (id: number) => adminService.deleteClient(id),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.all });

      toast({

        title: t('admin.clients.clientDeleted') || 'تم حذف العميل',

        description: t('admin.clients.clientDeletedDesc') || 'تم حذف العميل بنجاح',

      });

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('admin.clients.deleteFailed') || 'فشل الحذف',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useCreateClient = (onSuccess?: () => void) => {

  const queryClient = useQueryClient();

  const { toast } = useToast();

  const { t } = useLanguage();



  return useMutation({

    mutationFn: (data: CreateClientRequest) => adminService.createClient(data),

    onSuccess: () => {

      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.all });

      toast({

        title: t('admin.clients.clientCreated') || 'تم إنشاء العميل',

        description: t('admin.clients.clientCreatedDesc') || 'تم إنشاء العميل بنجاح',

      });

      onSuccess?.();

    },

    onError: (error) => {

      const errorMessage = getErrorMessage(error);

      toast({

        title: t('admin.clients.createFailed') || 'فشل إنشاء العميل',

        description: errorMessage,

        variant: 'destructive',

      });

    },

  });

};



export const useUpdateClient = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateClientRequest }) =>
      adminService.updateClient(id, data),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminClients.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.adminClients.detail(variables.id),
      });

      toast({
        title: t('admin.clients.updated') || 'تم تحديث العميل',
        description: t('admin.clients.updatedDesc') || 'تم تحديث بيانات العميل بنجاح',
      });

      onSuccess?.();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('admin.clients.updateFailed') || 'فشل التحديث',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};



// Specializations

export const useAdminSpecializations = (options?: { enabled?: boolean }) => {

  return useQuery({

    queryKey: ['admin', 'specializations'],

    queryFn: () => adminService.getSpecializations(),

    staleTime: 5 * 60 * 1000, // 5 minutes

    enabled: options?.enabled !== false,

  });

};



// Document Management

export const useAddTenderDocuments = (onSuccess?: () => void) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ tenderId, documents, documentTypes }: { 
      tenderId: number; 
      documents: File[]; 
      documentTypes?: string[] 
    }) => adminService.addTenderDocuments(tenderId, documents, documentTypes),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders', 'detail', variables.tenderId] });
      toast({
        title: t('tenders.documentsUploaded') || 'تم رفع المستندات',
        description: t('tenders.documentsUploadedDesc') || 'تم رفع المستندات بنجاح',
      });
      onSuccess?.();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('tenders.documentsUploadFailed') || 'فشل رفع المستندات',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteTenderDocument = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ tenderId, documentId }: { tenderId: number; documentId: number }) =>
      adminService.deleteTenderDocument(tenderId, documentId),
    onSuccess: (_response, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenders', 'detail', variables.tenderId] });
      toast({
        title: t('tenders.documentDeleted') || 'تم حذف المستند',
        description: t('tenders.documentDeletedDesc') || 'تم حذف المستند بنجاح',
      });
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast({
        title: t('tenders.documentDeleteFailed') || 'فشل حذف المستند',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });
};

